import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ga4 } from '@/lib/gtag';

export type UserIntent = 'rewards' | 'jackpot' | 'exposure';

export interface BreachData {
  name: string;
  title: string;
  domain: string;
  date: string;
  addedDate: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  dataTypes: string[];
  description: string;
  logoPath: string;
  pwnCount: number;
  isFabricated: boolean;
  isSpamList: boolean;
  isMalware: boolean;
  isStealerLog: boolean;
}

export interface ProfileAttribute {
  key: string;
  value: string;
  source: string;
}

// Unveil API types
export interface UnveilAddress {
  address: string;
  city: string;
  state: string;
  zip: string;
}

export interface AccountAttributes {
  first_name: string | null;
  last_name: string | null;
  gender: string | null;
  age: string | null;
  marital_status: string | null;
  education_level: string | null;
  addresses: UnveilAddress[];
  number_of_children: string | null;
}

export interface LookupResponse {
  email: string;
  hasBreaches: boolean;
  breachData: BreachData[];
  hasProfile: boolean;
  profileData: ProfileAttribute[];
  entriesAwarded: number;
  calculation: {
    base: number;
    profileBonus: number;
    breachBonus: number;
    criticalBonus: number;
  };
  lookupType: 'breach' | 'profile' | 'both' | 'simulated';
  // Unveil API data
  accountAttributes: AccountAttributes | null;
  hasUnveilData: boolean;
  // Count data for onboarding display
  brandCount?: number;
  dataBrokerCount?: number;
}

export type FastAuthStep = 'intent' | 'email' | 'loading' | 'reveal' | 'magic-link' | 'creating' | 'complete';

export interface FastAuthState {
  step: FastAuthStep;
  email: string | null;
  intent: UserIntent[];
  lookupData: LookupResponse | null;
  entriesAwarded: number;
  isLoading: boolean;
  error: string | null;
  linkSent: boolean;
  resendCooldown: number;
}

interface UseFastAuthOptions {
  referralCode?: string | null;
  source?: string;
  medium?: string;
  campaign?: string;
  initialEmail?: string;
  redirectTo?: string;
  onComplete?: () => void;
}

const SCAN_CACHE_KEY = 'reklaim_scan_cache';
const SCAN_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

function readScanCache(email: string): LookupResponse | null {
  try {
    const raw = localStorage.getItem(SCAN_CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw) as { email: string; data: LookupResponse; ts: number };
    if (
      cached.email.toLowerCase() === email.toLowerCase() &&
      Date.now() - cached.ts < SCAN_CACHE_TTL_MS
    ) {
      return cached.data;
    }
  } catch {
    // ignore malformed cache
  }
  return null;
}

function clearScanCache() {
  try { localStorage.removeItem(SCAN_CACHE_KEY); } catch { /* ignore */ }
}

export function useFastAuth(options?: UseFastAuthOptions) {
  // If initialEmail is provided, start at loading step
  const initialStep: FastAuthStep = options?.initialEmail ? 'loading' : 'intent';
  
  const [state, setState] = useState<FastAuthState>({
    step: initialStep,
    email: options?.initialEmail || null,
    intent: options?.initialEmail ? ['rewards', 'jackpot', 'exposure'] : [],
    lookupData: null,
    entriesAwarded: 0,
    isLoading: false,
    error: null,
    linkSent: false,
    resendCooldown: 0,
  });

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (state.resendCooldown > 0) {
      const timer = setTimeout(() => {
        setState(prev => ({ ...prev, resendCooldown: prev.resendCooldown - 1 }));
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [state.resendCooldown]);

  const setIntent = useCallback((intents: UserIntent[]) => {
    setState(prev => ({ 
      ...prev, 
      intent: intents, 
      step: 'email',
      error: null 
    }));
  }, []);

  const submitEmail = useCallback(async (email: string, intents?: UserIntent[]) => {
    const usedIntents = intents || state.intent;
    setState(prev => ({ ...prev, step: 'loading', email, intent: usedIntents, isLoading: true, error: null }));

    try {
      // Check if we already have a fresh scan result from the /protect page (< 10 min old)
      const cached = readScanCache(email);
      let data: LookupResponse;

      if (cached) {
        console.log('[useFastAuth] Using cached scan result — skipping email-lookup API call');
        data = cached;
        clearScanCache(); // consume once, don't reuse on refresh
      } else {
        // Race the lookup against a 15s timeout so the UI never hangs indefinitely
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Scan timed out. Please try again.')), 15_000)
        );
        const response = await Promise.race([
          supabase.functions.invoke('email-lookup', { body: { email, intent: usedIntents } }),
          timeoutPromise,
        ]);

        if (response.error) {
          throw new Error(response.error.message || 'Lookup failed');
        }
        data = response.data as LookupResponse;
      }

      setState(prev => ({
        ...prev,
        step: 'reveal',
        lookupData: data,
        entriesAwarded: data.entriesAwarded,
        isLoading: false,
      }));
    } catch (error) {
      console.error('Email lookup error:', error);
      setState(prev => ({
        ...prev,
        step: 'email',
        isLoading: false,
        error: error instanceof Error ? error.message : 'Something went wrong',
      }));
    }
  }, [state.intent]);

  const sendOtp = useCallback(async () => {
    if (!state.email) return;
    
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const redirectPath = options?.redirectTo ?? '/dashboard';
      const { error } = await supabase.auth.signInWithOtp({
        email: state.email,
        options: {
          emailRedirectTo: `${window.location.origin}${redirectPath}`,
          shouldCreateUser: true,
        }
      });
      
      if (error) throw error;
      
      setState(prev => ({ 
        ...prev, 
        step: 'magic-link', 
        linkSent: true,
        resendCooldown: 60,
        isLoading: false 
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to send link',
      }));
    }
  }, [state.email]);

  const verifyOtp = useCallback(async (otpCode: string) => {
    if (!state.email) return;
    
    setState(prev => ({ ...prev, step: 'creating', isLoading: true, error: null }));
    
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email: state.email,
        token: otpCode,
        type: 'email'
      });
      
      if (error) throw error;
      
      const userId = data.user?.id;
      
      if (userId) {
        // Check if new user (no profile completion) vs returning user
        const { data: profile } = await supabase
          .from('profiles')
          .select('onboarding_status')
          .eq('id', userId)
          .single();
        
        const isNewUser = profile?.onboarding_status !== 'completed';
        
        if (isNewUser) {
          // Update profile with intent, attribution, and mark onboarding complete
          const updates: Record<string, unknown> = {
            signup_intent: state.intent,
            onboarding_status: 'completed',
            onboarding_completed_at: new Date().toISOString(),
          };
          // Store terms acceptance timestamp if user accepted during signup
          const termsAcceptedAt = localStorage.getItem('reklaim_terms_accepted_at');
          if (termsAcceptedAt) {
            updates.terms_accepted_at = termsAcceptedAt;
            localStorage.removeItem('reklaim_terms_accepted_at');
          }
          if (options?.source) updates.signup_source = options.source;
          if (options?.medium) updates.signup_medium = options.medium;
          if (options?.campaign) updates.signup_campaign = options.campaign;

          await supabase
            .from('profiles')
            .update(updates)
            .eq('id', userId);

          // B3: trigger-migration is now ONLY invoked by AuthProvider's SIGNED_IN
          // handler (which guards on migration_triggered_at). Removing the duplicate
          // here eliminates the race and double-trigger risk.

          // Process referral
          if (options?.referralCode) {
            const { data: referrer } = await supabase
              .from('profiles')
              .select('id')
              .eq('referral_code', options.referralCode.toUpperCase())
              .single();

            if (referrer && referrer.id !== userId) {
              await supabase.from('referrals').insert({
                referrer_id: referrer.id,
                referred_id: userId,
                referral_code: options.referralCode.toUpperCase(),
                referrer_entries_awarded: 25,
                referred_entries_awarded: 25,
                is_completed: true,
              });

              await supabase
                .from('profiles')
                .update({ referred_by: referrer.id })
                .eq('id', userId);
            }
          }

          // Record Terms of Service consent for GDPR compliance
          try {
            await supabase.from('user_consents').insert({
              user_id: userId,
              consent_type: 'terms_of_service',
              granted: true,
              granted_at: new Date().toISOString(),
              user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
            });
          } catch (consentError) {
            console.error('Failed to record ToS consent:', consentError);
            // Don't block signup if consent recording fails
          }

          // Store breach data with full HIBP enrichment
          const nowIso = new Date().toISOString();
          if (state.lookupData?.breachData && state.lookupData.breachData.length > 0) {
            const breachRecords = state.lookupData.breachData.map(breach => ({
              user_id: userId!,
              breach_name: breach.name,
              breach_date: breach.date,
              severity: breach.severity,
              exposed_data_types: breach.dataTypes,
              source: 'hibp',
              data_source: 'hibp_live',
              last_verified_at: nowIso,
              title: breach.title || breach.name,
              domain: breach.domain || null,
              description: breach.description || null,
              logo_path: breach.logoPath || null,
              pwn_count: breach.pwnCount || null,
              added_date: breach.addedDate || null,
              is_spam_list: breach.isSpamList || false,
              is_malware: breach.isMalware || false,
              is_stealer_log: breach.isStealerLog || false,
            }));

            await supabase.from('breach_data').insert(breachRecords);
          }

          // Persist scan timestamp + Unveil cache to profile so the dashboard
          // shows accurate staleness and identity data immediately after signup
          const profileUpdates: Record<string, unknown> = {
            last_breach_scan_at: nowIso,
          };
          const unveilAttrs = state.lookupData?.accountAttributes;
          if (unveilAttrs && (unveilAttrs.first_name || unveilAttrs.last_name || (unveilAttrs.addresses?.length ?? 0) > 0)) {
            profileUpdates.unveil_attributes = unveilAttrs;
            profileUpdates.unveil_cached_at = nowIso;
          }
          await supabase.from('profiles').update(profileUpdates).eq('id', userId!);

          // Award signup points securely via edge function
          // Only award base + profile bonus (not breach bonus - that's Protect, not Earn)
          const earnEntries = state.lookupData 
            ? state.lookupData.calculation.base + state.lookupData.calculation.profileBonus
            : state.entriesAwarded;
            
          if (earnEntries > 0) {
            try {
              await supabase.functions.invoke('award-points', {
                body: {
                  amount: earnEntries,
                  transactionType: 'signup',
                  description: 'Signup bonus: welcome + profile data',
                },
              });
            } catch (pointsError) {
              console.error('Failed to award signup points:', pointsError);
              // Don't block signup if points fail
            }
          }

          // Send welcome email
          try {
            await supabase.functions.invoke('send-welcome', {
              body: {
                user_id: userId,
                email: state.email,
                entries_awarded: state.entriesAwarded,
              },
            });
          } catch (welcomeError) {
            // Don't block signup if welcome email fails
            console.error('Failed to send welcome notifications:', welcomeError);
          }

          // Track signup completion in GA4
          ga4.signupComplete('email');

          // Mark this email as a converted lead (fire-and-forget, non-blocking)
          supabase
            .from('protect_scan_leads')
            .update({
              converted_at: new Date().toISOString(),
              converted_user_id: userId,
            })
            .eq('email', (state.email ?? '').toLowerCase())
            .then(() => {/* ignore result */});
        }
      }
      
      setState(prev => ({ ...prev, step: 'complete', isLoading: false }));
      options?.onComplete?.();
    } catch (error) {
      console.error('Magic link verification error:', error);
      setState(prev => ({
        ...prev,
        step: 'magic-link',
        isLoading: false,
        error: error instanceof Error ? error.message : 'Verification failed',
      }));
    }
  }, [state.email, state.intent, state.lookupData, state.entriesAwarded, options]);

  const reset = useCallback(() => {
    setState({
      step: 'intent',
      email: null,
      intent: [],
      lookupData: null,
      entriesAwarded: 0,
      isLoading: false,
      error: null,
      linkSent: false,
      resendCooldown: 0,
    });
  }, []);

  /**
   * Demo-only: bypasses inbox delivery for the single configured demo email.
   * Calls the demo-otp-bypass edge function, which returns a real session if
   * (and only if) the email matches DEMO_BYPASS_EMAIL on the server.
   */
  const demoBypass = useCallback(async (email: string) => {
    setState(prev => ({ ...prev, step: 'creating', isLoading: true, error: null, email }));
    try {
      const { data, error } = await supabase.functions.invoke('demo-otp-bypass', {
        body: { email },
      });
      if (error) throw error;
      if (!data?.access_token || !data?.refresh_token) {
        throw new Error('Demo bypass did not return a session');
      }
      const { error: setErr } = await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      });
      if (setErr) throw setErr;
      setState(prev => ({ ...prev, step: 'complete', isLoading: false }));
      options?.onComplete?.();
    } catch (error) {
      console.error('Demo bypass error:', error);
      setState(prev => ({
        ...prev,
        step: 'email',
        isLoading: false,
        error: error instanceof Error ? error.message : 'Demo bypass failed',
      }));
    }
  }, [options]);

  return {
    ...state,
    setIntent,
    submitEmail,
    sendOtp,
    verifyOtp,
    demoBypass,
    reset,
  };
}
